import React, { useEffect, useState } from "react";
import { Outlet, Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { Menu, ShoppingBag, Heart, LogOut, LayoutDashboard, Store, Mountain, Compass, Leaf, Smile, Landmark, Palette, Flower2, MessageSquare, DollarSign } from "lucide-react";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const CAT_TABS = [
  { id: "adventure",   name: "Adventure",   Icon: Mountain },
  { id: "explore",     name: "Explore",     Icon: Compass },
  { id: "spiritual",   name: "Spiritual",   Icon: Flower2 },
  { id: "historic",    name: "Historic",    Icon: Landmark },
  { id: "cultural",    name: "Cultural",    Icon: Palette },
  { id: "relaxation",  name: "Relaxation",  Icon: Leaf },
  { id: "medical",     name: "Medical",     Icon: Heart },
  { id: "fun",         name: "Fun",         Icon: Smile },
];

// ---------- UserMenu ----------
function UserMenu({ user, onLogout }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 rounded-full p-1 pr-3 hover:bg-muted transition-colors" data-testid="user-menu-trigger">
          <Avatar className="h-8 w-8">
            <AvatarImage src={user.picture} alt={user.name} />
            <AvatarFallback>{(user.name || user.email || "U").slice(0, 1).toUpperCase()}</AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium hidden sm:inline">{user.name?.split(" ")[0] || "Account"}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-popover">
        <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {user.role === "vendor" ? (
          <>
            <DropdownMenuItem asChild><Link to="/vendor" data-testid="menu-vendor-dashboard"><LayoutDashboard className="h-4 w-4 mr-2" /> Vendor dashboard</Link></DropdownMenuItem>
            <DropdownMenuItem asChild><Link to="/vendor/listings" data-testid="menu-vendor-listings"><Store className="h-4 w-4 mr-2" /> My listings</Link></DropdownMenuItem>
            <DropdownMenuItem asChild><Link to="/vendor/bookings" data-testid="menu-vendor-bookings"><ShoppingBag className="h-4 w-4 mr-2" /> Bookings</Link></DropdownMenuItem>
            <DropdownMenuItem asChild><Link to="/vendor/messages" data-testid="menu-vendor-messages"><MessageSquare className="h-4 w-4 mr-2" /> Inquiries</Link></DropdownMenuItem>
            <DropdownMenuItem asChild><Link to="/vendor/reconciliation" data-testid="menu-vendor-reconciliation"><DollarSign className="h-4 w-4 mr-2" /> Earnings & Payouts</Link></DropdownMenuItem>
          </>
        ) : user.role === "admin" ? (
          <>
            <DropdownMenuItem asChild><Link to="/admin" data-testid="menu-admin-dashboard"><LayoutDashboard className="h-4 w-4 mr-2" /> Admin dashboard</Link></DropdownMenuItem>
            <DropdownMenuItem asChild><Link to="/search" data-testid="menu-admin-browse"><Store className="h-4 w-4 mr-2" /> Browse listings</Link></DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuItem asChild><Link to="/dashboard" data-testid="menu-tourist-dashboard"><LayoutDashboard className="h-4 w-4 mr-2" /> My trips</Link></DropdownMenuItem>
            <DropdownMenuItem asChild><Link to="/dashboard?tab=wishlist" data-testid="menu-wishlist"><Heart className="h-4 w-4 mr-2" /> Wishlist</Link></DropdownMenuItem>
            <DropdownMenuItem asChild><Link to="/become-a-vendor" data-testid="menu-become-vendor"><Store className="h-4 w-4 mr-2" /> Become a vendor</Link></DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogout} data-testid="menu-logout"><LogOut className="h-4 w-4 mr-2" /> Logout</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------- CategoryStrip ----------
function CategoryStrip() {
  return (
    <div className="border-b border-border bg-[hsl(var(--cream))] relative" data-testid="category-tabs-strip">
      <div className="mx-auto max-w-7xl px-2 sm:px-4 lg:px-8">
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar py-2">
          {CAT_TABS.map((c) => (
            <NavLink
              key={c.id}
              to={`/categories/${c.id}`}
              className={({ isActive }) =>
                `group shrink-0 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium whitespace-nowrap border transition-colors ${
                  isActive
                    ? "bg-[hsl(var(--primary))] text-white border-transparent shadow-[0_6px_16px_-10px_rgba(14,107,168,0.6)]"
                    : "bg-card border-border text-foreground/80 hover:text-foreground hover:bg-muted"
                }`
              }
              data-testid={`cat-tab-${c.id}`}
            >
              <c.Icon className="h-4 w-4" />
              {c.name}
            </NavLink>
          ))}
          <div className="ml-auto hidden md:flex items-center gap-1">
            <NavLink
              to="/search"
              className={({ isActive }) =>
                `shrink-0 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium border ${isActive ? "bg-foreground text-background border-transparent" : "bg-card border-border hover:bg-muted"}`
              }
              data-testid="cat-tab-all"
            >
              All experiences
            </NavLink>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- AppFooter ----------
function AppFooter() {
  return (
    <footer className="border-t border-border bg-[hsl(var(--deep-indigo))] text-white relative">
      <div className="h-[3px] iv-tricolor" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4 text-sm">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-[hsl(var(--saffron))] to-[hsl(var(--vermilion))] text-white"><Mountain className="h-4 w-4" /></span>
            <span className="font-display text-lg font-semibold">IndoVoyage</span>
          </div>
          <p className="text-white/70">Discover India, curated. Handpicked experiences by vetted local experts for international travelers.</p>
        </div>
        <div>
          <h4 className="font-semibold mb-2 text-white/90">Explore</h4>
          <ul className="space-y-1 text-white/70">
            {CAT_TABS.slice(0, 4).map((c) => (
              <li key={c.id}><Link className="hover:text-white" to={`/categories/${c.id}`}>{c.name}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-2 text-white/90">For travelers</h4>
          <ul className="space-y-1 text-white/70">
            <li><Link className="hover:text-white" to="/auth/register">Create account</Link></li>
            <li><Link className="hover:text-white" to="/dashboard">My trips</Link></li>
            <li><Link className="hover:text-white" to="/dashboard?tab=wishlist">Wishlist</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-2 text-white/90">For vendors</h4>
          <ul className="space-y-1 text-white/70">
            <li><Link className="hover:text-white" to="/become-a-vendor">List your experience</Link></li>
            <li><Link className="hover:text-white" to="/vendor">Vendor dashboard</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-white/60">
        &copy; {new Date().getFullYear()} IndoVoyage. Transparent, all-inclusive pricing for travelers.
      </div>
    </footer>
  );
}

// ---------- Layout ----------
export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    let mounted = true;
    const loadCart = async () => {
      if (!user) { setCartCount(0); return; }
      try { const r = await api.get("/cart"); if (mounted) setCartCount(r.data.items?.length || 0); } catch (e) {}
    };
    loadCart();
    return () => { mounted = false; };
  }, [user, location.pathname]);

  const onLogout = async () => {
    // Navigate first to leave any protected route before setUser(null) fires
    navigate("/", { replace: true });
    await logout();
  };

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-sm" data-testid="top-nav">
        <div className="h-[3px] iv-tricolor" />
        <div className="border-b border-border">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
              <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[hsl(var(--saffron))] via-[hsl(var(--vermilion))] to-[hsl(var(--rose-indian))] text-white shadow-[0_6px_16px_-6px_rgba(204,38,62,0.6)]">
                <Mountain className="h-5 w-5" />
              </span>
              <span className="font-display text-xl font-semibold tracking-tight">
                Indo<span className="text-[hsl(var(--vermilion))]">Voyage</span>
              </span>
            </Link>

            <div className="flex items-center gap-2">
              <Link to="/become-a-vendor" className="hidden md:inline-flex">
                <Button variant="ghost" className="rounded-xl" data-testid="become-vendor-nav-button">
                  <Store className="h-4 w-4 mr-2" /> For Vendors
                </Button>
              </Link>

              {user && (
                <Link to="/cart" className="relative" data-testid="nav-cart-link">
                  <Button variant="ghost" size="icon" className="rounded-xl">
                    <ShoppingBag className="h-5 w-5" />
                  </Button>
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-[hsl(var(--vermilion))] text-white text-[10px] font-semibold rounded-full h-5 min-w-5 px-1 flex items-center justify-center" data-testid="nav-cart-badge">{cartCount}</span>
                  )}
                </Link>
              )}

              {user ? (
                <UserMenu user={user} onLogout={onLogout} />
              ) : (
                <div className="flex items-center gap-2">
                  <Link to="/auth/login"><Button variant="ghost" className="rounded-xl" data-testid="nav-login-button">Log in</Button></Link>
                  <Link to="/auth/register"><Button className="rounded-xl bg-gradient-to-r from-[hsl(var(--vermilion))] to-[hsl(var(--saffron))] text-white hover:brightness-110" data-testid="nav-signup-button">Sign up</Button></Link>
                </div>
              )}

              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden rounded-xl" data-testid="mobile-menu-button">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-background">
                  <div className="mt-6 grid gap-1">
                    {CAT_TABS.map((c) => (
                      <Link key={c.id} to={`/categories/${c.id}`} className="px-3 py-3 rounded-lg hover:bg-muted text-base inline-flex items-center gap-2" data-testid={`mobile-nav-cat-${c.id}`}>
                        <c.Icon className="h-4 w-4 text-muted-foreground" /> {c.name}
                      </Link>
                    ))}
                    <Link to="/become-a-vendor" className="px-3 py-3 rounded-lg hover:bg-muted text-base">Become a vendor</Link>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
        <CategoryStrip />
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <AppFooter />
    </div>
  );
}
